import { Flex, IconButton, Popover, Text, Tooltip } from '@buffer-mono/popcorn'

import type {
  ReactionType,
  SupportedReaction,
} from '../../hooks/configuration/useConfigurationQuery'

import styles from './ReactionPicker.module.css'

import type { JSX } from 'react'

type ReactionPickerProps = {
  supportedReactions: SupportedReaction[]
  /**
   * Display name of the source network. When set, a caption explains that
   * reactions sync one way — from Buffer to the network, never back.
   */
  networkName?: string
  onReactionSelect: (reactionType: ReactionType) => void
  children: React.ReactNode
  open?: boolean
  animateIn?: boolean
  onOpenChange?: (open: boolean) => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  /**
   * When the picker is opened via keyboard, focus should move into the
   * content so the reactions are reachable. For hover-driven opens we keep
   * focus on the trigger to avoid yanking it away from the pointer user.
   */
  autoFocusOnOpen?: boolean
}

const ReactionItem = ({
  reaction,
  onSelect,
}: {
  reaction: SupportedReaction
  onSelect: (reactionType: ReactionType) => void
}): JSX.Element => {
  const reactionType = reaction.reactionType
  const reactionName =
    reactionType.charAt(0).toUpperCase() + reactionType.slice(1)

  if (reaction.visual.type === 'image') {
    return (
      <Tooltip content={reactionName} side="top" sideOffset={8}>
        <IconButton
          label={reactionName}
          variant="tertiary"
          size="small"
          className={styles.reactionItem}
          onClick={(): void => {
            onSelect(reactionType)
          }}
        >
          <img
            src={reaction.visual.url}
            alt=""
            className={styles.reactionImage}
          />
        </IconButton>
      </Tooltip>
    )
  }

  return (
    <Tooltip content={reactionName} side="top" sideOffset={8}>
      <IconButton
        label={reactionName}
        variant="tertiary"
        size="small"
        className={styles.reactionItem}
        onClick={(): void => {
          onSelect(reactionType)
        }}
      >
        <span className={styles.reactionEmoji} aria-hidden="true">
          {reaction.visual.icon}
        </span>
      </IconButton>
    </Tooltip>
  )
}

export const ReactionPicker = ({
  supportedReactions,
  networkName,
  onReactionSelect,
  children,
  open,
  animateIn = open ?? false,
  onOpenChange,
  onMouseEnter,
  onMouseLeave,
  autoFocusOnOpen = false,
}: ReactionPickerProps): JSX.Element => {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content
        side="top"
        align="center"
        sideOffset={4}
        className={`${styles.popoverContent} ${
          animateIn ? styles.animateIn : styles.animateOut
        }`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onOpenAutoFocus={(e): void => {
          if (!autoFocusOnOpen) e.preventDefault()
        }}
        onClick={(e): void => e.stopPropagation()}
      >
        <Flex direction="column" gap="sm" align="center">
          {networkName && (
            <Text size="sm" color="subtle" className={styles.syncCaveat}>
              {`Buffer syncs reactions to ${networkName}, but reactions added there won't show here.`}
            </Text>
          )}
          <Flex gap="md" align="center" justify="center">
            {supportedReactions.map((reaction) => (
              <ReactionItem
                key={reaction.reactionType}
                reaction={reaction}
                onSelect={onReactionSelect}
              />
            ))}
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover>
  )
}
